import { reactive } from 'vue'
import { Post } from '@/model/data-object/Post'
import useUserBlock from '@/composables/userBlock/userBlock'
import useReadManager from '@/composables/read/readManager'
import usePostFactory from '@/composables/post/postFactory'

export class PostsRoot {
  id: number
  post?: Post
  children: PostsRoot[] = []
  parentPost?: Post
  fullyLoaded = false
  mounted = false
  missingParent = false
  error = null

  constructor(id: number, post?: Post) {
    this.post = post
    this.id = id
  }
}

const postRootDict = reactive<PostsRoot[]>([])
const postList = reactive<PostsRoot[]>([])
const postTree = reactive<PostsRoot[]>([])
let lastRenderPosition = 0

export default function usePostList() {
  const { isUserBlocked } = useUserBlock()
  const { createFromObject } = usePostFactory()

  function findPostRoot(id: number): PostsRoot | undefined {
    if (typeof postRootDict[id] !== 'undefined') {
      return postRootDict[id] as PostsRoot
    }

    return undefined
  }

  function addPostRootToMap(post: PostsRoot) {
    postRootDict[post.id] = post
  }

  function resolveFullyLoaded(remainingCount: number): void {
    if (postTree.length === 0) {
      return
    }
    const notLoadedPosts = postTree.filter(postRoot => !postRoot.fullyLoaded)
    const lastRootId = postTree[postTree.length - 1].id
    for (let i = 0; i < notLoadedPosts.length; i++) {
      if (
        typeof notLoadedPosts[i].post !== 'undefined' &&
        notLoadedPosts[i].id !== lastRootId
      ) {
        notLoadedPosts[i].fullyLoaded = true
      }
    }

    if (remainingCount === 0) {
      postTree[postTree.length - 1].fullyLoaded = true
    }
  }

  function findPostRootIndex(id: number): number {
    return postTree.findIndex(postsRoot => postsRoot.id === id)
  }

  function addPostMeta(post: Post) {
    // todo move to post factory?
    const { updateIsNew } = useReadManager()
    post.postMeta.position = lastRenderPosition
    post.postMeta.blockedByUser = isUserBlocked(post.author.id)
    lastRenderPosition++
    updateIsNew(post)
  }

  const createRoot = (post: Post): PostsRoot => {
    addPostMeta(post)

    const root = new PostsRoot(post.id, post)
    if (post.parent) {
      const parentPostRoot = findPostRoot(post.parent)
      if (typeof parentPostRoot === 'undefined') {
        root.missingParent = true
      } else {
        root.parentPost = parentPostRoot.post

        parentPostRoot.children.push(root)
      }
    }

    addPostRootToMap(root)

    return root
  }

  const clearListData = () => {
    postList.length = 0
    postTree.length = 0
    postRootDict.length = 0
    lastRenderPosition = 0
  }

  const appendToPostList = (postRoot: PostsRoot, checkDuplicate = false) => {
    if (checkDuplicate) {
      const post = postList.find(root => root.id === postRoot.id)
      if (post) {
        return
      }
    }
    postList.push(postRoot)
  }

  const prepareVirtualRoot = (post: Post) => {
    if (post.parent === null) {
      return
    }

    const virtualRootIds = post.path
      .split('-')
      .map(stringId => parseInt(stringId))
      .filter(rootId => rootId !== post.id)
      .reverse()

    let child: PostsRoot | undefined = undefined
    virtualRootIds.forEach(virtualRootId => {
      const virtualRoot = new PostsRoot(virtualRootId)
      if (child) {
        virtualRoot.children.push(child)
      }
      child = virtualRoot
      addPostRootToMap(virtualRoot)
    })

    if (child) {
      postTree.push(child)
    }
  }

  const addToList = (
    posts: Array<Post>,
    checkDuplicate = false
  ): Array<PostsRoot> => {
    const newRootList = []
    for (let i = 0; i < posts.length; i++) {
      const post = createFromObject(posts[i])
      const root = createRoot(post)

      appendToPostList(root, checkDuplicate)
      newRootList.push(root)
      if (posts[i].parent === null) {
        postTree.push(root)
      }
    }

    return newRootList
  }

  const prependToList = (posts: Array<Post>) => {
    const tmpPostTree = []
    const tmpPostList = []

    for (let i = 0; i < posts.length; i++) {
      if (i === 0) {
        prepareVirtualRoot(posts[i])
      }

      const postRoot = findPostRoot(posts[i].id)
      if (postRoot) {
        postRoot.post = createFromObject(posts[i])
        addPostMeta(postRoot.post)

        continue
      }

      const post = createFromObject(posts[i])
      const root = createRoot(post)
      tmpPostList.push(root)
      if (posts[i].parent === null) {
        tmpPostTree.push(root)
      }
    }

    postList.unshift(...tmpPostList)
    postTree.unshift(...tmpPostTree)
  }

  const getLastPostRoot = (): PostsRoot | undefined => {
    if (postList.length > 0) {
      return postList[postList.length - 1] as PostsRoot
    }

    return undefined
  }

  const getFirstPostRootWithPost = (): PostsRoot | undefined => {
    const postRoot = postList.find(
      postRoot => typeof postRoot.post !== 'undefined'
    )

    if (postRoot) {
      return postRoot as PostsRoot
    }
    return undefined
  }

  const getLastId = (): number | undefined => {
    const lastPost = getLastPostRoot()
    if (lastPost) {
      return lastPost.id
    }

    return undefined
  }

  const getFirstId = (): number | undefined => {
    const lastPost = getFirstPostRootWithPost()
    if (lastPost) {
      return lastPost.id
    }

    return undefined
  }

  const getFirstPost = (): Post | undefined => {
    const lastPost = getFirstPostRootWithPost()
    if (lastPost) {
      return lastPost.post
    }

    return undefined
  }

  const getLastPost = (): Post | undefined => {
    const lastPost = getLastPostRoot()
    if (lastPost) {
      return lastPost.post
    }

    return undefined
  }

  function addPostToTree(postProxy: Post): PostsRoot {
    const post = createFromObject(postProxy)
    const postRoot = createRoot(post)
    appendToPostList(postRoot)
    if (!post.parent) {
      postTree.unshift(postRoot)
    }

    return postRoot
  }

  return {
    postList,
    postTree,
    findPostRoot,
    getFirstPost,
    getLastPost,
    clearListData,
    addToList,
    getLastId,
    getFirstId,
    prependToList,
    prepareVirtualRoot,
    addPostToTree,
    findPostRootIndex,
    resolveFullyLoaded,
  }
}
